import { authedRequest } from '../api/requests'

export interface UserRolesResponse {
  roles: string[]
  hasManagerRole: boolean
  hasAdminRole: boolean
}

class RoleService {
  private rolesCache: string[] | null = null
  private cacheTimestamp: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  async fetchUserRoles(): Promise<string[]> {
    try {
      const response = await authedRequest('BaseBiz', '/CurrentUserRoles', 'GET')

      if (response.isSuccess && response.entity) {
        this.rolesCache = response.entity as string[]
        this.cacheTimestamp = Date.now()
        return this.rolesCache
      }

      return []
    } catch (error) {
      console.error('Failed to fetch user roles:', error)
      return []
    }
  }

  async getUserRoles(forceRefresh: boolean = false): Promise<string[]> {
    const now = Date.now()
    const isCacheValid = this.rolesCache !== null && now - this.cacheTimestamp < this.CACHE_DURATION

    if (!forceRefresh && isCacheValid) {
      return this.rolesCache!
    }

    return await this.fetchUserRoles()
  }

  async hasRole(roleCode: string): Promise<boolean> {
    const roles = await this.getUserRoles()
    return roles.includes(roleCode)
  }

  async hasManagerRole(): Promise<boolean> {
    return await this.hasRole('Manager')
  }

  async hasAdminRole(): Promise<boolean> {
    return await this.hasRole('Admin')
  }

  async getUserRoleInfo(): Promise<UserRolesResponse> {
    const roles = await this.getUserRoles()
    return {
      roles,
      hasManagerRole: roles.includes('Manager'),
      hasAdminRole: roles.includes('Admin'),
    }
  }

  clearCache(): void {
    this.rolesCache = null
    this.cacheTimestamp = 0
  }
}

export const roleService = new RoleService()
